import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

const DATA_URL = process.env.PUBLIC_URL + '/data/social-media-metrics.json';

function useSocialData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${DATA_URL}?t=${Date.now()}`);
      const json = await res.json();
      setData(json);
      setLastUpdated(new Date());
    } catch (e) {
      console.warn('Could not load social media data:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);


  return { data, loading, lastUpdated, refresh: load };
}

// Stat card component
function StatCard({ label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// Follower chart
function FollowerChart({ history }) {
  if (!history || history.length < 2) {
    return <div className="empty">Follower data will appear as the campaign runs.</div>;
  }
  const chartData = history.map(h => ({
    date: new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    followers: h.followers,
  }));
  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e2a3e" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7f99' }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#6b7f99' }} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ background: '#141922', border: '1px solid #2a3347', borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: '#94a3b8' }}
            formatter={(v) => [v, 'Followers']}
          />
          <Line type="monotone" dataKey="followers" stroke="#818cf8" strokeWidth={2}
            dot={false} activeDot={{ r: 4, fill: '#818cf8' }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Engagement bar chart
function EngagementChart({ posts }) {
  if (!posts || posts.length === 0) {
    return <div className="empty">Post data will appear as the campaign runs.</div>;
  }
  const chartData = posts.slice(-7).map(p => ({
    date: new Date(p.posted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    likes: p.likes || 0,
    retweets: p.retweets || 0,
    replies: p.replies || 0,
  }));
  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e2a3e" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7f99' }} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#6b7f99' }} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ background: '#141922', border: '1px solid #2a3347', borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: '#94a3b8' }}
          />
          <Legend wrapperStyle={{ fontSize: 11, color: '#6b7f99' }} />
          <Bar dataKey="likes" fill="#818cf8" name="Likes" radius={[3, 3, 0, 0]} />
          <Bar dataKey="retweets" fill="#34d399" name="Retweets" radius={[3, 3, 0, 0]} />
          <Bar dataKey="replies" fill="#f59e0b" name="Replies" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Campaign pie chart
function CampaignBreakdown({ campaigns }) {
  if (!campaigns || campaigns.length === 0) {
    return <div className="empty">No campaign data yet.</div>;
  }
  const COLORS = ['#818cf8', '#34d399', '#f59e0b', '#f87171'];
  const data = campaigns.map((c, i) => ({
    name: c.name,
    value: c.posts?.length || 0,
    color: COLORS[i % COLORS.length],
  })).filter(d => d.value > 0);
  if (data.length === 0) {
    return <div className="empty">No posts logged yet.</div>;
  }
  return (
    <div className="chart-wrap">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" outerRadius={80} dataKey="value"
            label={({ name, value }) => `${name}: ${value} posts`}
            labelLine={false}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ background: '#141922', border: '1px solid #2a3347', borderRadius: 8, fontSize: 12 }}
            formatter={(v, name) => [v, name]}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// Modal for full post view
function PostModal({ post, onClose }) {
  if (!post) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Full Post</h3>
          <button onClick={onClose} className="modal-close">✕</button>
        </div>
        <div className="modal-meta">
          <span>{new Date(post.posted_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
          <span>{new Date(post.posted_at).toLocaleTimeString()}</span>
          <span className="modal-campaign">{post.campaign_id?.replace(/-/g, ' ').replace('artemis ii campaign', 'Artemis II') || ''}</span>
        </div>
        <div className="modal-body">{post.text}</div>
        <div className="modal-stats">
          <span>❤️ {post.likes || 0} Likes</span>
          <span>🔁 {post.retweets || 0} Retweets</span>
          <span>💬 {post.replies || 0} Replies</span>
          {post.url && <a href={post.url} target="_blank" rel="noopener noreferrer" className="modal-link">View on X ↗</a>}
        </div>
      </div>
    </div>
  );
}

// Recent posts table
function RecentPosts({ posts }) {
  const [selectedPost, setSelectedPost] = useState(null);

  if (!posts || posts.length === 0) {
    return <div className="empty">No posts yet. Check back after the next scheduled post.</div>;
  }
  return (
    <>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Campaign</th>
            <th>Content Preview</th>
            <th>Likes</th>
            <th>RTs</th>
            <th>Replies</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {posts.slice(-10).reverse().map((p, i) => (
            <tr key={p.posted_at + i}>
              <td style={{ color: '#6b7f99' }}>{new Date(p.posted_at).toLocaleDateString()}</td>
              <td style={{ color: '#818cf8' }}>{p.campaign_id?.replace(/-/g, ' ').replace('artemis ii campaign', 'Artemis II') || '—'}</td>
              <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.text?.substring(0, 60)}{p.text?.length > 60 ? '…' : ''}
              </td>
              <td>{p.likes || 0}</td>
              <td>{p.retweets || 0}</td>
              <td>{p.replies || 0}</td>
              <td><button onClick={() => setSelectedPost(p)} className="expand-btn">Expand</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {selectedPost && <PostModal post={selectedPost} onClose={() => setSelectedPost(null)} />}
    </>
  );
}

// Main App
export default function App() {
  const { data, loading, lastUpdated, refresh } = useSocialData(30000);

  return (
    <div className="dashboard">
      <div className="top-bar">
        <div className="top-bar-title">
          <h1>📊 Social Media Dashboard</h1>
          <div className="subtitle">
            {data?.campaigns?.find(c => c.status === 'active')
              ? `📍 Active Campaign: ${data.campaigns.find(c => c.status === 'active').name}`
              : 'No active campaign'}
          </div>
        </div>
        <div className="top-bar-right">
          <span className="last-updated">
            {data?.updated_at
              ? `Updated ${new Date(data.updated_at).toLocaleString()}`
              : 'Loading…'}
          </span>
          <button onClick={refresh} className="refresh-btn">↻</button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading social media data…</div>
      ) : (
        <>
          <div className="stats-grid">
            <StatCard label="Total Posts" value={data?.total_posts || 0} color="#818cf8" />
            <StatCard label="Followers" value={data?.total_followers || 0} color="#34d399" />
            <StatCard label="Likes" value={data?.total_likes || 0} color="#f87171" />
            <StatCard label="Retweets" value={data?.total_retweets || 0} color="#f59e0b" />
            <StatCard label="Replies" value={data?.total_replies || 0} color="#60a5fa" />
          </div>

          <div className="charts-grid">
            <div className="chart-card">
              <h2>Follower Growth</h2>
              <div style={{ height: 220 }}>
                <FollowerChart history={data?.follower_history} />
              </div>
            </div>
            <div className="chart-card">
              <h2>Weekly Engagement</h2>
              <div style={{ height: 220 }}>
                <EngagementChart posts={data?.campaigns?.flatMap(c => c.posts || [])} />
              </div>
            </div>
            <div className="chart-card">
              <h2>Campaign Breakdown</h2>
              <div style={{ height: 220 }}>
                <CampaignBreakdown campaigns={data?.campaigns} />
              </div>
            </div>
          </div>

          <div className="table-card">
            <h2>Recent Posts</h2>
            <RecentPosts posts={data?.campaigns?.flatMap(c => c.posts || [])} />
          </div>

          {data?.campaigns?.find(c => c.status === 'active') && (
            <div className="table-card">
              <h2>Active Campaign: {data.campaigns.find(c => c.status === 'active').name}</h2>
              <div className="campaign-info">
                <span>Theme: {data.campaigns.find(c => c.status === 'active').theme}</span>
                <span>Running: {data.campaigns.find(c => c.status === 'active').start_date} → {data.campaigns.find(c => c.status === 'active').end_date}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
